import { Volume2, Pause, Play, Square } from "lucide-react";
import { useListen } from "./listen/useListen";

/* Listen to this page — UI only; playback logic lives in ./listen/useListen.
   Device Web Speech when a voice exists, Cloud Voices (AI narration) otherwise. */
export default function ListenButton({ targetId, label = "Listen to this article" }) {
  const { supported, state, rate, setRate, toggle, stop, modeRef } = useListen(targetId);
  if (!supported) return null;

  return (
    <div className="inline-flex items-center gap-1.5" data-testid="listen-controls">
      <button onClick={toggle} data-testid="listen-toggle"
        className="inline-flex items-center gap-1.5 px-3 py-2 border border-emerald-600 text-emerald-700 text-sm font-700 hover:bg-emerald-50 transition-colors">
        {state === "playing" ? <Pause className="w-4 h-4" /> : state === "paused" ? <Play className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
        {state === "idle" ? label : state === "playing" ? "Pause" : "Resume"}
      </button>
      {state !== "idle" && (
        <button onClick={stop} data-testid="listen-stop" title="Stop"
          className="p-2 border border-zinc-300 text-zinc-500 hover:text-rose-600 hover:border-rose-400">
          <Square className="w-4 h-4" />
        </button>
      )}
      {state === "idle" && (
        <select value={rate} onChange={(e) => setRate(Number(e.target.value))} data-testid="listen-rate"
          className="border border-zinc-300 bg-white px-1.5 py-2 text-xs" title="Reading speed">
          <option value={0.75}>0.75× slow</option>
          <option value={0.9}>0.9× relaxed</option>
          <option value={1}>1× normal</option>
          <option value={1.2}>1.2×</option>
          <option value={1.5}>1.5×</option>
        </select>
      )}

      {/* Floating player — always visible while audio is active, especially for mobile */}
      {state !== "idle" && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-[70] flex items-center gap-2 bg-zinc-900/95 text-white shadow-2xl rounded-full pl-4 pr-2 py-2 backdrop-blur border border-white/10"
          style={{ paddingBottom: "calc(0.5rem + env(safe-area-inset-bottom, 0px) / 2)" }} data-testid="listen-float-bar">
          <span className="flex items-end gap-0.5 h-4 shrink-0" aria-hidden="true">
            {[0, 1, 2].map((i) => (
              <span key={i} className={`w-1 bg-emerald-400 rounded-sm ${state === "playing" ? "animate-pulse" : "opacity-40"}`}
                style={{ height: `${8 + i * 4}px`, animationDelay: `${i * 150}ms` }} />
            ))}
          </span>
          <span className="text-xs font-600 max-w-[130px] sm:max-w-[240px] truncate">
            {state === "playing" ? (modeRef.current === "cloud" ? "AI narration — tap any paragraph to jump" : "Reading aloud — tap any paragraph to jump") : "Paused"}
          </span>
          <button onClick={toggle} data-testid="listen-float-toggle" aria-label={state === "playing" ? "Pause audio" : "Resume audio"}
            className="p-2.5 rounded-full bg-white/10 hover:bg-white/20 active:scale-95 transition-transform">
            {state === "playing" ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          </button>
          <button onClick={stop} data-testid="listen-float-stop" aria-label="Stop audio"
            className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 hover:bg-rose-500 active:scale-95 transition-transform px-4 py-2.5 text-sm font-700">
            <Square className="w-4 h-4" /> Stop
          </button>
        </div>
      )}
    </div>
  );
}
